import { useEffect, useMemo, useRef, useState } from "react";

import { FilterDefinition } from "src/uniform/contenttypes/filterDefinition";

import { GALLERY_SEARCH_EVENT } from "../constants";
import { buildUniformFilterObject } from "../utils/filterEngine";
import {
    filtersToUniformFilterObject,
    readStateFromUrl,
    uniformFilterObjectToFlat,
    writeStateToUrl
} from "../utils/urlSync";

import { useFireFilterClickAnalytics } from "./use-fire-filter-click-analytics";

import type { UniformFilterObject } from "../utils/types";

type DropdownVisibility = Record<string, boolean>;

interface FilterStateOptions {
    readonly items: unknown[];
    readonly filterDefs: FilterDefinition[];
    readonly urlSync: boolean;
    readonly optionsDataSources?: Record<string, unknown[]>;
}

/**
 * Owns all transient filter/search/sort UI state and the handlers that mutate
 * it. Optionally hydrates from + persists to the URL query string.
 *
 * Returns:
 * - State (`filterObject`, `searchQuery`, `selectedSortId`, `dropdownVisibility`)
 * - Derived flags (`isAnyDropdownOpen`, `closedDropdownState`)
 * - Handlers for every UI affordance
 */
export const useFilterState = ({
    items,
    filterDefs,
    urlSync,
    optionsDataSources
}: FilterStateOptions) => {
    const fireFilterClickAnalytics = useFireFilterClickAnalytics();

    const [filterObject, setFilterObject] = useState<UniformFilterObject>(() =>
        buildUniformFilterObject(items, filterDefs, optionsDataSources)
    );
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedSortId, setSelectedSortId] = useState<string | undefined>();
    const [dropdownVisibility, setDropdownVisibility] =
        useState<DropdownVisibility>({});

    const closedDropdownState = useMemo<DropdownVisibility>(
        () => Object.fromEntries(filterDefs.map((def) => [def.id, false])),
        [filterDefs]
    );

    // Rebuild filter options when the filter schema or data shape changes,
    // preserving any existing user selections for options that still exist.
    // We use item count as a proxy for "items actually changed" to avoid
    // resetting selections when Uniform recreates data object references
    // on re-renders without changing the actual item set.
    const itemCount = items.length;
    useEffect(() => {
        const fresh = buildUniformFilterObject(
            items,
            filterDefs,
            optionsDataSources
        );
        setFilterObject((prev) => {
            // Merge: carry over existing selections for options that still exist.
            return Object.fromEntries(
                Object.entries(fresh).map(([filterId, subFilter]) => [
                    filterId,
                    Object.fromEntries(
                        Object.entries(subFilter).map(([optionId, option]) => [
                            optionId,
                            prev[filterId]?.[optionId] ?? option
                        ])
                    )
                ])
            );
        });
        setDropdownVisibility(
            Object.fromEntries(filterDefs.map((def) => [def.id, false]))
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [itemCount, filterDefs, optionsDataSources]);

    // Disable URL sync in Uniform preview — writing query params breaks the preview iframe.
    // Computed once on mount via useRef since the preview state never changes mid-session.
    const isUniformPreviewRef = useRef(
        typeof window !== "undefined" &&
            window.location.pathname.includes("/uniform-preview")
    );
    const effectiveUrlSync = urlSync && !isUniformPreviewRef.current;

    // Hydrate from URL on mount + on browser back/forward.
    useEffect(() => {
        if (!effectiveUrlSync || typeof window === "undefined") return;

        const hydrate = () => {
            const urlState = readStateFromUrl(
                window.location.search,
                filterDefs
            );
            setFilterObject((prev) =>
                filtersToUniformFilterObject(urlState.filters, prev)
            );
            setSearchQuery(urlState.query);
            setSelectedSortId(urlState.sortId);
        };

        hydrate();
        window.addEventListener("popstate", hydrate);
        return () => window.removeEventListener("popstate", hydrate);
    }, [urlSync, filterDefs]);

    useEffect(() => {
        if (typeof window === "undefined") return;

        const handleSearchEvent = (e: Event) => {
            const { query } = (e as CustomEvent<{ query: string }>).detail;
            setSearchQuery(query ?? "");
        };

        window.addEventListener(GALLERY_SEARCH_EVENT, handleSearchEvent);
        return () =>
            window.removeEventListener(GALLERY_SEARCH_EVENT, handleSearchEvent);
    }, []);

    const isAnyDropdownOpen = Object.values(dropdownVisibility).some(Boolean);

    const pushUrl = (
        nextFilterObject: UniformFilterObject,
        nextQuery: string,
        nextSortId: string | undefined
    ) => {
        if (!effectiveUrlSync) return;
        writeStateToUrl({
            filters: uniformFilterObjectToFlat(nextFilterObject),
            query: nextQuery,
            sortId: nextSortId
        });
    };

    const handleFilterButtonClick = (filterId: string) => {
        fireFilterClickAnalytics(filterId);
        setDropdownVisibility((prev) => ({
            ...closedDropdownState,
            [filterId]: !prev[filterId]
        }));
    };

    const toggleFilterOption = (filterId: string, optionId: string) => {
        setFilterObject((prev) => {
            const subFilter = prev[filterId] ?? {};
            const current = subFilter[optionId];
            const nextActive = !current?.stagedActive;
            const next = {
                ...prev,
                [filterId]: {
                    ...subFilter,
                    [optionId]: {
                        committedActive: nextActive,
                        stagedActive: nextActive
                    }
                }
            };
            // Commit and push URL immediately so filters apply on selection,
            // not on dropdown close.
            pushUrl(next, searchQuery, selectedSortId);
            return next;
        });
    };

    const clearAllFilters = () => {
        const cleared = mapAllOptionsToInactive(filterObject);
        setFilterObject(cleared);
        pushUrl(cleared, searchQuery, selectedSortId);
    };

    const handleSearchSubmit = () => {
        const nextFilterObject = searchQuery
            ? mapAllOptionsToInactive(filterObject)
            : filterObject;
        setFilterObject(nextFilterObject);
        pushUrl(nextFilterObject, searchQuery, selectedSortId);
    };

    const handleSearchClear = () => {
        setSearchQuery("");
        pushUrl(filterObject, "", selectedSortId);
    };

    const handleSortChange = (nextSortId: string) => {
        setSelectedSortId(nextSortId);
        pushUrl(filterObject, searchQuery, nextSortId);
    };

    const closeAllDropdownsAndCommit = () => {
        setDropdownVisibility(closedDropdownState);
    };

    return {
        filterObject,
        searchQuery,
        selectedSortId,
        dropdownVisibility,
        isAnyDropdownOpen,
        setSearchQuery,
        handleFilterButtonClick,
        toggleFilterOption,
        clearAllFilters,
        handleSearchSubmit,
        handleSearchClear,
        handleSortChange,
        closeAllDropdownsAndCommit
    };
};

const mapAllOptionsToInactive = (
    state: UniformFilterObject
): UniformFilterObject =>
    Object.fromEntries(
        Object.entries(state).map(([filterId, subFilter]) => [
            filterId,
            Object.fromEntries(
                Object.keys(subFilter ?? {}).map((optionId) => [
                    optionId,
                    { committedActive: false, stagedActive: false }
                ])
            )
        ])
    );
